/**
 * @file PitchSurface — token-coloured tactical field and positioned content.
 * @description Geometry from Figma xE5Jd227ZgDwnFzRDJzxWG, 255:7101.
 * Colours/radius use its bound outline/default, surface/card and radius/lg.
 * @module components/atoms/PitchSurface
 */
import React from "react";
import { View, type LayoutChangeEvent } from "react-native";
import Svg, { Circle, G, Path, Rect } from "react-native-svg";
import { cssInterop } from "nativewind";
import { Box } from "../Box";

cssInterop(Svg, {
  className: { target: "style", nativeStyleToProp: { color: true } },
});

/** Content anchored in the reference field's 570 × 694 coordinate space. */
export interface PitchSurfacePosition {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly content: React.ReactNode;
  readonly onLayout?: (event: LayoutChangeEvent) => void;
}

/** Field presentation; caller owns all player state and actions. */
export interface PitchSurfaceProps {
  readonly positions?: readonly PitchSurfacePosition[];
  readonly children?: React.ReactNode;
  readonly testID?: string;
  readonly accessibilityLabel?: string;
  readonly onLayout?: (event: LayoutChangeEvent) => void;
  /**
   * Caps the rendered width in px (the aspect ratio then sets the height), so
   * a caller can fit the whole field inside a height (SE-8526). Omitted = the
   * 570 px reference cap alone.
   */
  readonly maxWidth?: number;
}

/** Fills the surface to measure it (absoluteFill, without StyleSheet). */
const MEASURE_FILL = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
} as const;

/**
 * Renders the shared reference markings and optional tactical anchors.
 * @param props - Positioned content and accessibility identity.
 * @param props.positions - Anchors in the reference coordinate system.
 * @param props.children - Fallback layout for other formations.
 * @param props.testID - Test identity forwarded to the surface.
 * @param props.accessibilityLabel - Accessible name for the surface.
 * @param props.onLayout - Reports the field geometry to its owning container.
 * @param props.maxWidth - Caps the rendered width in px, to fit a height.
 * @returns The field surface.
 */
export function PitchSurface({
  positions = [],
  children,
  testID,
  accessibilityLabel,
  onLayout,
  maxWidth,
}: PitchSurfaceProps) {
  return (
    <Box
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      // design-system: runtime-computed style waiver — the fit width is measured, not a token.
      UNSAFE_style={maxWidth === undefined ? undefined : { maxWidth }}
      className="relative aspect-pitch-surface w-full max-w-pitch-surface overflow-hidden rounded-lg border border-outline-default bg-surface-card"
    >
      <Svg
        width="100%"
        height="100%"
        viewBox="0 0 570 694"
        className="absolute inset-0 text-outline-default"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Rect x={0} y={0} width={570} height={694} />
        <Path d="M0 347H570" />
        <Circle cx={285} cy={347} r={69} />
        <G fill="currentColor" stroke="none">
          <Circle cx={285} cy={347} r={2.5} />
          <Circle cx={285} cy={74.952} r={2.5} />
          <Circle cx={285} cy={619.048} r={2.5} />
        </G>
        <Path d="M101.175 0V109.652H468.825V0 M201.78 0V38.17H368.22V0 M239.4 0V-9H330.6V0 M101.175 694V584.348H468.825V694 M201.78 694V655.83H368.22V694 M239.4 694V703H330.6V694" />
        <G transform="translate(273.486 109.652)">
          <Path d="M23.028 0C20.306418 14.499 16.041983 23.028 11.514 23.028C6.986266 23.028 2.721582 14.499 0 0" />
        </G>
        <G transform="translate(273.486 561.319824)">
          <Path d="M23.028 23.028C20.306418 8.529 16.041983 0 11.514 0C6.986266 0 2.721582 8.529 0 23.028" />
        </G>
        <Path d="M15 0C15 8.284 8.284 15 0 15 M570 15C561.716 15 555 8.284 555 0 M555 694C555 685.716 561.716 679 570 679 M0 679C8.284 679 15 685.716 15 694" />
      </Svg>
      {positions.map(position => (
        <View
          key={position.id}
          testID={`pitch-anchor-${position.id}`}
          onLayout={position.onLayout}
          style={{
            position: "absolute",
            left: `${(position.x / 570) * 100}%`,
            top: `${(position.y / 694) * 100}%`,
          }}
        >
          {position.content}
        </View>
      ))}
      {children}
      {/* SE-8535: on web this Box is a raw div, which never fires onLayout;
          a View filling the surface reports its size on every platform. */}
      {onLayout ? (
        <View
          testID="pitch-surface-measure"
          pointerEvents="none"
          onLayout={onLayout}
          style={MEASURE_FILL}
        />
      ) : null}
    </Box>
  );
}
